import { Button } from '@gerdau/hefesto/Button';
import { Icon } from '@gerdau/hefesto/Icon';
import { Tooltip } from 'antd';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch } from 'react-redux';
import theme from '../../../../../../../../assets/styles/theme/theme';
import { EMeasureUnit } from '../../../../../../../../store/commomTypes';
import { loadTrackingLongSteel } from '../../../../../../../../store/ducks/long-steel-orders/actions';
import {
  IOpenOrderData,
  ITrackingLongSteelItemData
} from '../../../../../../../../store/ducks/long-steel-orders/types';
import {
  Container,
  TrackingCircle,
  TrackingCircleLoading,
  TrackingContainer,
  TrackingItem
} from './styles';

interface ITrackingLongSteelItem {
  title: ETrackingLongSteelItemTitle;
  trackingData: ITrackingLongSteelItemData[];
  totalValue?: number;
  measureUnit?: EMeasureUnit;
}
interface ITrackingLongSteelProps {
  item: IOpenOrderData;
}

enum ETrackingLongSteelItemTitle {
  REQUESTED = 'Pedido implantado',
  CONFIRMED = 'Pedido confirmado',
  DELIVERY = 'Remessa gerada',
  SHIPMENTS = 'Transporte contratado',
  BILLED = 'Faturado'
}

const defaultDateFormat = 'YYYY-MM-DD';

const TrackingLongSteel: React.FC<ITrackingLongSteelProps> = ({ item }) => {
  const dispatch = useDispatch();
  const [
    trackingItemsDataAlreadyInitialized,
    setTrackingItemsDataAlreadyInitialized
  ] = useState(false);

  const [data, setData] = useState<ITrackingLongSteelItem[]>([
    {
      title: ETrackingLongSteelItemTitle.REQUESTED,
      totalValue: Number(item.quantities.requestedQuantity),
      measureUnit: item.quantities.requestedMeasureUnit,
      trackingData: [
        {
          date: item.itemCreationDate,
          measureUnit: item.quantities.requestedMeasureUnit,
          quantity: item.quantities.requestedQuantity
        }
      ]
    },
    {
      title: ETrackingLongSteelItemTitle.CONFIRMED,
      totalValue: item.futureDeliveries.reduce(
        (acc, curr) => parseFloat(curr.estimatedDeliveryQuantity || '0') + acc,
        0
      ),
      measureUnit: item.quantities.requestedMeasureUnit,
      trackingData: item.futureDeliveries.map((delivery) => {
        let deliveryDate;
        if (delivery.estimatedDeliveryDate.length > 10) {
          // caso retorne no formato "DD/MM/YYYY à DD/MM/YYYY"
          const splitedDate = delivery.estimatedDeliveryDate.split(' ');
          deliveryDate = moment(
            splitedDate[splitedDate.length - 1],
            'DD/MM/YYYY'
          );
        } else {
          deliveryDate = moment(delivery.estimatedDeliveryDate, 'DD/MM/YYYY');
        }

        return {
          date: deliveryDate.format(defaultDateFormat),
          quantity: delivery.estimatedDeliveryQuantity,
          measureUnit: delivery.estimatedDeliveryMeasureUnit
        };
      })
    },
    {
      title: ETrackingLongSteelItemTitle.DELIVERY,
      trackingData: []
    },
    {
      title: ETrackingLongSteelItemTitle.SHIPMENTS,
      trackingData: []
    },
    {
      title: ETrackingLongSteelItemTitle.BILLED,
      trackingData: []
    }
  ]);

  const initTrackingItemsData = () => {
    const updateTrackingLongSteelItem = (
      updatedDataArray: ITrackingLongSteelItem[],
      title: ETrackingLongSteelItemTitle,
      totalValue: number,
      measureUnit: EMeasureUnit,
      trackingData: ITrackingLongSteelItemData[]
    ): ITrackingLongSteelItem[] => {
      const idx = updatedDataArray.findIndex((ti) => ti.title === title);

      updatedDataArray[idx] = {
        title,
        totalValue,
        measureUnit,
        trackingData
      };

      return updatedDataArray;
    };

    const longSteelTracking = item.trackingLongSteel?.longSteel;

    if (
      !data ||
      !longSteelTracking ||
      !longSteelTracking.deliveries ||
      !longSteelTracking.shipments ||
      !longSteelTracking.billing
    ) {
      return;
    }

    const { deliveries, shipments, billing } = longSteelTracking;

    let updateData = data;

    updateData = updateTrackingLongSteelItem(
      updateData,
      ETrackingLongSteelItemTitle.DELIVERY,
      Number(deliveries.deliveryQuantity),
      deliveries.measureUnit || item.quantities.requestedMeasureUnit,
      deliveries.tracking
    );
    updateData = updateTrackingLongSteelItem(
      updateData,
      ETrackingLongSteelItemTitle.SHIPMENTS,
      Number(shipments.shipmentQuantity),
      shipments.measureUnit || item.quantities.requestedMeasureUnit,
      shipments.tracking
    );
    updateData = updateTrackingLongSteelItem(
      updateData,
      ETrackingLongSteelItemTitle.BILLED,
      Number(billing.billingQuantity),
      billing.measureUnit || item.quantities.requestedMeasureUnit,
      billing.tracking
    );

    setTrackingItemsDataAlreadyInitialized(true);
    setData(updateData);
  };

  const loadTracking = () => {
    const { id, salesOrder, salesOrderItem } = item;
    const customerId = item.soldTo.code;
    dispatch(loadTrackingLongSteel(id, customerId, salesOrder, salesOrderItem));
  };

  useEffect(() => {
    if (!item.trackingLongSteel) {
      loadTracking();
    } else if (
      data &&
      item.trackingLongSteel &&
      !item.trackingLongSteel.loading &&
      !item.trackingLongSteel.error &&
      !trackingItemsDataAlreadyInitialized
    ) {
      initTrackingItemsData();
    }
  });

  const getPercentage = (value: number): number => {
    const fieldValue = value;
    const total = Number(item.quantities.requestedQuantity);
    return (fieldValue / total) * 100;
  };

  const getTrackingCircleValue = (
    trackingItem: ITrackingLongSteelItem
  ): string => {
    if (
      trackingItem.totalValue &&
      trackingItem.totalValue < 1 &&
      trackingItem.totalValue > 0
    ) {
      const idxOfDot = trackingItem.totalValue.toString().indexOf('.');
      const value = trackingItem.totalValue
        .toString()
        .substring(0, idxOfDot + 3);
      return `${value}${trackingItem.measureUnit}`;
    } else if (trackingItem.totalValue && trackingItem.totalValue > 0) {
      return `${Math.trunc(trackingItem.totalValue)}${
        trackingItem.measureUnit
      }`;
    } else {
      return '-';
    }
  };

  const getTrackingCircleSize = (trackingItem: ITrackingLongSteelItem) => {
    const trackingCircleValue = getTrackingCircleValue(trackingItem);
    if (trackingCircleValue.length >= 9) {
      return 'ees';
    } else if (trackingCircleValue.length > 6) {
      return 'es';
    } else if (trackingCircleValue.length > 5) {
      return 's';
    }
  };

  const getTrackingItemDate = (
    trackingItem: ITrackingLongSteelItem
  ): string => {
    if (trackingItem.title === ETrackingLongSteelItemTitle.CONFIRMED) {
      return '';
    }
    return trackingItem.trackingData.length > 0
      ? moment(
          trackingItem.trackingData[trackingItem.trackingData.length - 1].date,
          defaultDateFormat
        ).format('DD/MM/YYYY')
      : '--/--/--';
  };

  const getTrackingItemClassName = (trackingItem: ITrackingLongSteelItem) => {
    if (item.trackingLongSteel?.loading) {
      return 'loading';
    } else if (trackingItem.totalValue && trackingItem.totalValue > 0) {
      return 'checked';
    }
  };

  const canShowDeliveryTrackingItem = (): boolean => {
    return !['BRIN', 'BRIO'].includes(item.salesOrg);
  };

  return (
    <Container>
      <span>
        Pare o mouse sob os status para visualizar o histórico das datas.
      </span>

      <TrackingContainer>
        {data
          ?.filter((trackingItem) => {
            if (trackingItem.title === ETrackingLongSteelItemTitle.DELIVERY) {
              return canShowDeliveryTrackingItem();
            } else {
              return true;
            }
          })
          .map((trackingItem, idx) => (
            <TrackingItem
              key={idx}
              className={getTrackingItemClassName(trackingItem)}
            >
              <h4 className="title">{trackingItem.title}</h4>

              {trackingItem.title !== ETrackingLongSteelItemTitle.CONFIRMED ? (
                <Tooltip
                  overlayClassName="tracking-long-steel-tooltip"
                  trigger={
                    item.trackingLongSteel?.loading ? [] : ['hover', 'click']
                  }
                  title={
                    <>
                      {trackingItem.trackingData.map((td, i) => (
                        <div key={i}>
                          <span>
                            {`${moment(td.date, defaultDateFormat).format(
                              'DD/MM/YYYY'
                            )} - ${Number(td.quantity)}${td.measureUnit}`}
                          </span>
                          <br />
                        </div>
                      ))}

                      {trackingItem.trackingData.length > 1 && (
                        <span
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end'
                          }}
                        >
                          {`Total - ${Number(trackingItem.totalValue).toFixed(
                            3
                          )}${trackingItem.measureUnit}`}
                        </span>
                      )}

                      {trackingItem.trackingData.length === 0 && (
                        <span>Nenhuma data disponível.</span>
                      )}
                    </>
                  }
                >
                  {item.trackingLongSteel?.loading && (
                    <TrackingCircleLoading color={theme.colors.white2}>
                      <Skeleton
                        width={'3.25rem'}
                        height={'3.25rem'}
                        style={{ borderRadius: '50%' }}
                      />
                    </TrackingCircleLoading>
                  )}

                  {!item.trackingLongSteel?.loading && (
                    <TrackingCircle
                      className={
                        trackingItem.totalValue && trackingItem.totalValue > 0
                          ? 'checked'
                          : ''
                      }
                      checkedPercentage={getPercentage(
                        trackingItem.totalValue || 0
                      )}
                    >
                      <div className="checked"></div>

                      <span
                        className={`value ${getTrackingCircleSize(
                          trackingItem
                        )}`}
                      >
                        {`${getTrackingCircleValue(trackingItem)}`}
                      </span>
                    </TrackingCircle>
                  )}
                </Tooltip>
              ) : (
                <>
                  {item.trackingLongSteel?.loading && (
                    <TrackingCircleLoading color={theme.colors.white2}>
                      <Skeleton
                        width={'3.25rem'}
                        height={'3.25rem'}
                        style={{ borderRadius: '50%' }}
                      />
                    </TrackingCircleLoading>
                  )}

                  {!item.trackingLongSteel?.loading && (
                    <TrackingCircle
                      className={
                        trackingItem.totalValue && trackingItem.totalValue > 0
                          ? 'checked'
                          : ''
                      }
                      checkedPercentage={getPercentage(
                        trackingItem.totalValue || 0
                      )}
                    >
                      <div className="checked"></div>

                      <span
                        className={`value ${getTrackingCircleSize(
                          trackingItem
                        )}`}
                      >
                        {`${getTrackingCircleValue(trackingItem)}`}
                      </span>
                    </TrackingCircle>
                  )}
                </>
              )}

              <span className="date">
                {item.trackingLongSteel?.loading ? (
                  <Skeleton width={'3.25rem'} />
                ) : (
                  getTrackingItemDate(trackingItem)
                )}
              </span>
            </TrackingItem>
          ))}
      </TrackingContainer>

      {item.trackingLongSteel?.error && (
        <div className="error-container">
          <span>Falha ao carregar as informações de tracking.</span>
          <Button
            name="settingTable"
            onClick={loadTracking}
            variant="primary"
            icon={<Icon option="Settings" />}
            iconPosition="left"
          >
            Tentar novamente
          </Button>
        </div>
      )}
    </Container>
  );
};

export default TrackingLongSteel;
